/**
 * Export utilities for converting data to Excel/CSV formats
 */

/**
 * Export data to CSV file
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Name of the file (without extension)
 * @param {Array} columns - Array of column definitions: [{key: 'name', header: 'Name'}, ...]
 */
export const exportToCSV = (data, filename, columns) => {
  if (!data || data.length === 0) {
    alert("No data to export");
    return;
  }

  // Create CSV header
  const headers = columns.map((col) => col.header).join(",");

  // Create CSV rows
  const rows = data.map((row) => {
    return columns
      .map((col) => {
        let value = row[col.key];
        
        // Handle null/undefined
        if (value === null || value === undefined) {
          value = "";
        }
        
        // Format value if formatter provided
        if (col.formatter && typeof col.formatter === "function") {
          value = col.formatter(value, row);
        }
        
        // Escape quotes and wrap in quotes if contains comma
        value = String(value).replace(/"/g, '""');
        if (value.includes(",") || value.includes("\n") || value.includes('"')) {
          value = `"${value}"`;
        }
        
        return value;
      })
      .join(",");
  });

  // Combine header and rows
  const csv = [headers, ...rows].join("\n");

  // Create blob and download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export data to Excel-compatible CSV (UTF-8 with BOM)
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Name of the file (without extension)
 * @param {Array} columns - Array of column definitions
 */
export const exportToExcel = (data, filename, columns) => {
  if (!data || data.length === 0) {
    alert("No data to export");
    return;
  }

  // Create CSV content with BOM for Excel UTF-8 support
  const BOM = "\uFEFF";
  const headers = columns.map((col) => col.header).join(",");
  
  const rows = data.map((row) => {
    return columns
      .map((col) => {
        let value = row[col.key];
        
        if (value === null || value === undefined) {
          value = "";
        }
        
        if (col.formatter && typeof col.formatter === "function") {
          value = col.formatter(value, row);
        }
        
        value = String(value).replace(/"/g, '""');
        if (value.includes(",") || value.includes("\n") || value.includes('"')) {
          value = `"${value}"`;
        }
        
        return value;
      })
      .join(",");
  });

  const csv = BOM + [headers, ...rows].join("\n");

  // Create blob and download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export records data to Excel
 */
export const exportRecordsToExcel = (records, filename = "records") => {
  const columns = [
    { key: "name", header: "Name" },
    { key: "username", header: "Username" },
    { key: "resume_forwarded", header: "Resume Forwarded" },
    { key: "pipeline_unique", header: "Pipeline (Unique)" },
    { key: "pipeline", header: "Pipeline (Total)" },
    { key: "turned_up", header: "Turned Up" },
    { key: "selected", header: "Selected" },
    { key: "backout", header: "Backout" },
    { key: "not_selected", header: "Not Selected" },
    { key: "revenue", header: "Revenue (lac)", formatter: (val) => val ? parseFloat(val).toFixed(2) : "0.00" },
    { key: "target", header: "Target" },
  ];

  exportToExcel(records, filename, columns);
};

/**
 * Export candidate details to Excel
 */
export const exportCandidatesToExcel = (candidates, filename = "candidates") => {
  const columns = [
    { key: "candidate_name", header: "Candidate Name" },
    { key: "speciality", header: "Speciality" },
    { key: "hospital", header: "Hospital" },
    { key: "city", header: "City" },
    { key: "state", header: "State" },
    { key: "status", header: "Status" },
    { key: "revenue", header: "Revenue (lac)", formatter: (val) => val ? parseFloat(val).toFixed(2) : "0.00" },
    { key: "remark", header: "Remark" },
    { key: "date_entered", header: "Date Entered" },
  ];

  exportToExcel(candidates, filename, columns);
};

/**
 * Export monthly comparison data to Excel
 */
export const exportMonthlyComparisonToExcel = (data, filename = "monthly_comparison") => {
  // Flatten the data structure
  const flatData = [];
  
  Object.entries(data).forEach(([username, userData]) => {
    userData.records?.forEach((record) => {
      flatData.push({
        name: userData.name,
        username: username,
        month_start: record.month_start,
        month_end: record.month_end,
        resume_forwarded: record.total_resume_forwarded || 0,
        pipeline_unique: record.total_pipeline_unique || 0,
        pipeline: record.total_pipeline || 0,
        turned_up: record.total_turned_up || 0,
        selected: record.total_selected || 0,
        backout: record.total_backout || 0,
        not_selected: record.total_not_selected || 0,
        revenue: record.total_revenue || 0,
      });
    });
  });

  const columns = [
    { key: "name", header: "Name" },
    { key: "username", header: "Username" },
    { key: "month_start", header: "Month Start" },
    { key: "month_end", header: "Month End" },
    { key: "resume_forwarded", header: "Resume Forwarded" },
    { key: "pipeline_unique", header: "Pipeline (Unique)" },
    { key: "pipeline", header: "Pipeline (Total)" },
    { key: "turned_up", header: "Turned Up" },
    { key: "selected", header: "Selected" },
    { key: "backout", header: "Backout" },
    { key: "not_selected", header: "Not Selected" },
    { key: "revenue", header: "Revenue (lac)", formatter: (val) => parseFloat(val).toFixed(2) },
  ];

  exportToExcel(flatData, filename, columns);
};

